// features/auth/Login.js - Login component
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaGoogle, FaSignInAlt, FaLock } from 'react-icons/fa';
import { GoogleLogin } from '@react-oauth/google';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import LoadingOverlay from '../../components/LoadingOverlay';

const Login = () => {
  const { t } = useTranslation();
  const { login, socialLogin, loading, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the return URL from location state or default to home
  const from = location.state?.from?.pathname || '/';
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [formError, setFormError] = useState('');
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    try {
      await login(formData.email, formData.password);
      navigate(from, { replace: true });
    } catch (err) {
      setFormError(err.message || t('auth.loginError'));
    }
  };
  
  const handleGoogleLoginSuccess = async (credentialResponse) => {
    try {
      await socialLogin('google', {
        credential: credentialResponse.credential,
        clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID
      });
      navigate(from, { replace: true });
    } catch (err) {
      setFormError(err.message || t('auth.loginError'));
    }
  };
  
  const handleGoogleLoginError = () => {
    setFormError(t('auth.googleLoginError'));
  };
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        stiffness: 260, 
        damping: 20 
      } 
    }
  };
  
  return (
    <div className="flex justify-center items-center min-h-[80vh] p-4">
      {loading && <LoadingOverlay />}
      
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="w-full max-w-md"
      >
        <Card variant="glass" hover="glow" className="w-full">
          <CardHeader>
            <motion.div 
              className="w-16 h-16 rounded-2xl bg-primary bg-opacity-10 dark:bg-primary-dark dark:bg-opacity-20 flex items-center justify-center mb-4 mx-auto"
              variants={itemVariants}
            >
              <FaSignInAlt className="text-primary text-2xl" />
            </motion.div>
            <CardTitle gradient={true} className="text-center">{t('auth.loginTitle')}</CardTitle>
            <CardDescription className="text-center">{t('auth.loginSubtitle')}</CardDescription>
          </CardHeader>
          
          <CardContent>
            <motion.form onSubmit={handleSubmit} variants={containerVariants}>
              {(formError || error) && (
                <motion.div 
                  variants={itemVariants}
                  className="bg-red-100 dark:bg-red-900/30 border border-red-400 text-red-700 dark:text-red-300 px-4 py-3 rounded relative mb-4"
                >
                  {formError || error}
                </motion.div>
              )}
              
              <motion.div className="mb-4" variants={itemVariants}>
                <label htmlFor="email" className="block text-sm font-medium mb-1">
                  {t('auth.email')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder={t('auth.emailPlaceholder')}
                  required
                />
              </motion.div>
              
              <motion.div className="mb-6" variants={itemVariants}>
                <div className="flex justify-between items-center mb-1">
                  <label htmlFor="password" className="block text-sm font-medium">
                    {t('auth.password')}
                  </label>
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder={t('auth.passwordPlaceholder')}
                  required
                />
              </motion.div>
              
              <motion.div className="flex items-center mb-6" variants={itemVariants}>
                <input
                  type="checkbox"
                  id="rememberMe"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label htmlFor="rememberMe" className="ml-2 block text-sm">
                  {t('auth.rememberMe')}
                </label>
              </motion.div>
              
              <motion.div variants={itemVariants}>
                <Button
                  type="submit"
                  variant="default"
                  className="w-full"
                  animation="shimmer"
                  disabled={loading}
                >
                  {loading ? t('common.loading') : t('auth.signIn')}
                </Button>
              </motion.div>
            </motion.form>
            
            <motion.div 
              className="mt-6 relative flex items-center justify-center"
              variants={itemVariants}
            >
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-700"></div>
              </div>
              <div className="relative px-4 bg-background dark:bg-background-dark">
                {t('auth.orContinueWith')}
              </div>
            </motion.div>
            
            <motion.div className="mt-6" variants={itemVariants}>
              <div className="flex justify-center">
                <GoogleLogin
                  onSuccess={handleGoogleLoginSuccess}
                  onError={handleGoogleLoginError}
                  text="continue_with"
                  shape="pill"
                  theme="filled_blue"
                  size="large"
                  width="100%"
                />
              </div>
            </motion.div>
          </CardContent>
          
          <CardFooter className="flex justify-center">
            <motion.p variants={itemVariants} className="text-sm text-center">
              {t('auth.noAccount')} <Link to="/register" className="text-primary hover:underline">{t('auth.register')}</Link>
            </motion.p>
          </CardFooter>
        </Card>
      </motion.div>
    </div>
  );
};

export default Login;
